import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LayoutAnimation } from 'react-native';
import {
  Alert,
  AlertHandle,
  Body,
  Caption,
  DownloadOurApp,
  Headline,
  LogoHeader
} from 'src/components';
import { AlertOption } from 'src/components/Alert';
import { SlideItem } from 'src/components/kit/Slider/Slide';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import { Screens } from 'src/routes/stacks/screens';
import AccountLocated from './components/AccountLocated';
import ClientSearch from './components/ClientSearch';
import PracticeSearch from './components/PracticeSearch';
import PracticeSearchResults from './components/PracticeSearchResults';
import SearchHero from './components/SearchHero';
import { FormState, STATES, Actions, ReducerState, ACTIONS } from './helpers/model';
import { SliderElement, STEPS, TourGuide } from './tour';
import styled from 'styled-components/native';
import { Weight } from 'src/theme';
import openEmailToAlly from 'src/utils/openEmailToAlly';
import { makePhoneCall, openEmail } from 'src/utils';
import useGoBack from 'src/hooks/useGoBack';
import { ALLY_SUPPORT_EMAIL, IS_WEB, MOBILE_WIDTH_SMALL, Size } from 'src/constants';
import HeroImage from './components/HeroImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import LandingSidebar from 'src/components/LandingSidebar';

const SLIDES: SlideItem[] = [
  {
    title: 'practiceClientSearch:tour.drawerTitle',
    body: 'practiceClientSearch:tour.drawerBody',
    image: <SearchHero />
  }
];

const PracticeClientSearch = () => {
  const { t } = useTranslation('practiceClientSearch');
  const { colors, viewMode } = useAppTheme();
  const { navigate } = useNavigation();
  const goBack = useGoBack();

  const modalRef = useRef<AlertHandle>(null);

  const { top } = useSafeAreaInsets();

  const [state, dispatch] = useReducer(
    (state: ReducerState, { action, data }: Actions) => {
      const nextState = (state.state + 1) as FormState;
      const prevState = (state.state - 1) as FormState;
      const combinedData = { ...state.data, ...data };
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

      switch (action) {
        case ACTIONS.SHOW_MODAL:
          modalRef.current?.alert();
          return state;
        case ACTIONS.ADVANCE:
          if (state.state === FormState.ACCOUNT_LOCATED || !(nextState in FormState)) {
            return {
              ...state,
              exit: true
            };
          }
          return { ...STATES[nextState], data: state.data };
        case ACTIONS.GO_BACK:
          if (state.state === FormState.PRACTICE_SEARCH || !(prevState in FormState)) {
            return {
              ...state,
              exit: true
            };
          }
          return { ...STATES[prevState], data: state.data };
        case ACTIONS.UPDATE_DATA:
          return { ...state, data: combinedData };
      }
    },
    { ...STATES[FormState.PRACTICE_SEARCH], data: {} }
  );

  const [modalTitle, setModalTitle] = useState('');
  const [modalText, setModalText] = useState('');

  useEffect(() => {
    if (state.exit) {
      goBack();
    }
  }, [goBack, state.exit]);

  const options: AlertOption[] = useMemo(
    () =>
      state.state === FormState.CLIENT_SEARCH
        ? [
            {
              label: t('exit'),
              type: 'neutral',
              action: () => navigate(Screens.LANDING)
            },
            {
              label: t('practiceClientSearch:tryAgain')
            }
          ]
        : [
            {
              label: t('OK'),
              type: 'neutral'
            }
          ],
    [navigate, state.state, t]
  );

  const showModal = () => dispatch({ action: ACTIONS.SHOW_MODAL });
  const advance = () => dispatch({ action: ACTIONS.ADVANCE });
  const updateData = (data: Actions['data']) => dispatch({ action: ACTIONS.UPDATE_DATA, data });
  const moveBack = () => dispatch({ action: ACTIONS.GO_BACK });

  return (
    <TourGuide.Provider>
      <ResponsiveContainer>
        {viewMode.isWeb && <LandingSidebar />}
        <Container paddingTop={top}>
          <LogoHeader
            displayName={t('login:appName')}
            justify={viewMode.isMobile ? 'center' : 'flex-start'}
          />
          <FlexedView>
            <ContentContainer>
              {viewMode.isMobile &&
                state.state !== FormState.PRACTICE_SEARCH_RESULTS &&
                state.state !== FormState.ACCOUNT_LOCATED && <HeroImage />}
              <FormTitle>{t(state.title)}</FormTitle>
              <>
                {state.state === FormState.PRACTICE_SEARCH && (
                  <PracticeSearch
                    setModalTitle={setModalTitle}
                    setModalText={setModalText}
                    showAlert={showModal}
                    moveNext={advance}
                    updateData={updateData}
                    goBack={moveBack}
                  />
                )}
                {state.state === FormState.PRACTICE_SEARCH_RESULTS && (
                  <PracticeSearchResults
                    moveNext={advance}
                    updateData={updateData}
                    state={state}
                    goBack={moveBack}
                  />
                )}
                {state.state === FormState.CLIENT_SEARCH && (
                  <ClientSearch
                    setModalTitle={setModalTitle}
                    setModalText={setModalText}
                    showAlert={showModal}
                    moveNext={advance}
                    state={state}
                    updateData={updateData}
                    goBack={moveBack}
                  />
                )}
                {state.state === FormState.ACCOUNT_LOCATED && (
                  <AccountLocated state={state} moveNext={advance} />
                )}
              </>
            </ContentContainer>
          </FlexedView>
          <BottomContainer>
            {IS_WEB && !viewMode.isWeb && <DownloadOurApp />}
            <FootNote color={colors.onPrimary}>
              <Trans
                t={t}
                i18nKey={t('login:footNote')}
                values={{ ourEmail: ALLY_SUPPORT_EMAIL }}
                components={{
                  contactUs: (
                    <EmailLink
                      onPress={() => {
                        void openEmailToAlly();
                      }}
                    />
                  )
                }}
              />
            </FootNote>
          </BottomContainer>
        </Container>
      </ResponsiveContainer>

      <Alert
        title={modalTitle}
        body={
          <Trans
            t={t}
            components={{
              allyEmail: <Link onPress={openEmailToAlly} />,
              phone: (
                <Link onPress={() => makePhoneCall(state.data.selectedPractice?.phoneNumber)} />
              ),
              email: <Link onPress={async () => openEmail(state.data.selectedPractice?.email)} />
            }}
          >
            {modalText}
          </Trans>
        }
        ref={modalRef}
        options={options}
      />
      <SliderElement id={STEPS.DRAWER} slides={SLIDES} autoStart />
    </TourGuide.Provider>
  );
};

export default PracticeClientSearch;

const ResponsiveContainer = styled.View`
  flex-direction: ${({ theme }) => (theme.viewMode.isWeb ? 'row' : 'col')};
  flex: 1;
`;

const Container = styled(KeyboardAwareScrollView).attrs(({ theme }) => ({
  contentContainerStyle: {
    paddingTop: IS_WEB ? Size.S : 0,
    paddingHorizontal: theme.viewMode.isMobile ? Size.X2_S : Size.M,
    gap: Size.S,
    flex: !theme.viewMode.isMobile ? 1 : undefined
  }
}))<{ paddingTop: number }>`
  background-color: ${({ theme }) => theme.colors.primary};
  padding-top: ${({ paddingTop }) => paddingTop}px;
`;

const FlexedView = styled.View`
  ${({ theme }) => !theme.viewMode.isMobile && 'flex: 1;'}
  ${({ theme }) => !theme.viewMode.isMobile && 'justify-content: center;'}
`;

const ContentContainer = styled.View`
  background-color: ${({ theme }) => theme.colors.surface};
  border-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.roundness};
  ${({ theme }) => !theme.viewMode.isMobile && `width: ${MOBILE_WIDTH_SMALL}px;`}
  ${({ theme }) => !theme.viewMode.isMobile && `align-self: center;`}
`;

const Link = styled(Body)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.primary};
  font-weight: ${Weight.BOLD};
`;

const FormTitle = styled(Headline)`
  align-self: center;
  margin-top: ${Size.S}px;
`;

const BottomContainer = styled.View`
  ${({ theme }) => !theme.viewMode.isMobile && `justify-content: flex-end;`}
`;

const EmailLink = styled(Caption)`
  color: ${({ theme }) => theme.colors.onPrimary};
  text-decoration-line: underline;
  font-weight: ${Weight.MEDIUM};
  letter-spacing: ${Size.X5_S}px;
`;

const FootNote = styled(Caption)`
  padding-bottom: ${Size.S}px;
`;
